import { useLingui } from '@lingui/react/macro';

import { isInDraftStep, RequestWithRelationsFromRequestsList } from '@/models/request';
import { isExternalWorkshopUser } from '@/models/user';
import { SupervisorPicker } from '@/routes/Requests/components/SupervisorPicker';
import { useCurrentSession } from '@/services/auth';

import { RequestSelection } from '../hooks/useRequestSelection';

import { RequestDueAtCell } from './components/RequestDueAtCell';
import { StepDueDate } from './components/StepDueDate';
import { RequestTasks } from './common';
import { ColumnAndCell, RequestsTab } from './RequestsTab';

export const ToDoTab = ({
  requests,
  count,
  isLoading,
  isError,
  selection,
}: {
  requests: RequestWithRelationsFromRequestsList[];
  count?: number;
  isLoading: boolean;
  isError: boolean;
  selection: RequestSelection;
}) => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();

  const columnsAndCells: ColumnAndCell[] = [
    {
      column: {
        name: t({ id: 'requests.table.column.tasks.title', message: 'Tasks to do' }),
        width: '1.5fr',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <RequestTasks request={request} />
        ),
      },
    },
    {
      column: {
        name: t({ id: 'requests.table.column.step-due-date.title', message: 'Step due date' }),
        width: '1.5fr',
        sortId: 'statusDueAt',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <StepDueDate request={request} variant="row" />
        ),
      },
    },
    {
      hide: isExternalWorkshopUser(currentSession),
      column: {
        name: t({
          id: 'requests.table.column.request-due-date.title',
          message: 'Request due date',
        }),
        width: !isExternalWorkshopUser(currentSession) ? '1.5fr' : undefined,
        sortId: 'dueAt',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <RequestDueAtCell request={request} variant="row" />
        ),
      },
    },
    {
      column: {
        name: t({ id: 'requests.table.column.supervisor.title', message: 'Supervisor' }),
        width: '104px',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <SupervisorPicker request={request} variant="row" disabled={isInDraftStep(request)} />
        ),
        style: {
          justifyContent: 'center',
        },
      },
    },
  ];

  return (
    <RequestsTab
      requests={requests}
      count={count}
      isLoading={isLoading}
      columnsAndCellsForTab={columnsAndCells}
      cardElementsForTab={(request: RequestWithRelationsFromRequestsList) => (
        <CardElements request={request} />
      )}
      isError={isError}
      selection={selection}
    />
  );
};

const CardElements = ({ request }: { request: RequestWithRelationsFromRequestsList }) => (
  <>
    <RequestTasks request={request} />
    <StepDueDate request={request} variant="card" />
    <SupervisorPicker request={request} variant="card" disabled={isInDraftStep(request)} />
  </>
);
